import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * LocationInput Component
 * 
 * Enforces the shelf/bin location format: SHELF/BIN
 * Examples: B1-1/0001, C5-2/0250, CHIC/0001, TILL/0001
 * 
 * - Shelf: Alphanumeric, allows letters, numbers, and dashes (e.g., B1-1, CHIC, TILL)
 * - Bin: 4-digit number, auto-padded with zeros (e.g., 0001, 0250)
 */

// Parse a location string into shelf and bin parts
export const parseLocation = (location) => {
  if (!location || typeof location !== 'string') {
    return { shelf: '', bin: '' };
  }
  
  const parts = location.split('/');
  if (parts.length === 2) {
    return {
      shelf: parts[0].toUpperCase(),
      bin: parts[1]
    };
  }
  
  // If no slash, treat the whole thing as shelf
  return { shelf: location.toUpperCase(), bin: '' };
};

// Format shelf and bin into a location string
export const formatLocation = (shelf, bin) => {
  const cleanShelf = (shelf || '').trim().toUpperCase();
  const cleanBin = (bin || '').trim();
  
  if (!cleanShelf && !cleanBin) return '';
  if (!cleanBin) return cleanShelf;
  
  // Pad bin to 4 digits
  const paddedBin = cleanBin.replace(/\D/g, '').padStart(4, '0').slice(0, 4);
  
  return `${cleanShelf}/${paddedBin}`;
};

// Validate location format
export const validateLocation = (location) => {
  if (!location) return { valid: true, error: null }; // Empty is valid
  
  const parts = location.split('/');
  if (parts.length !== 2) {
    return { valid: false, error: 'Format must be SHELF/BIN (e.g., B1-1/0001)' };
  }
  
  const [shelf, bin] = parts;
  
  if (!shelf || shelf.length === 0) {
    return { valid: false, error: 'Shelf code is required' };
  }
  
  // Shelf can be alphanumeric with dashes
  if (!/^[A-Z0-9\-]+$/i.test(shelf)) {
    return { valid: false, error: 'Shelf can only contain letters, numbers, and dashes' };
  }
  
  // Bin must be 4 digits
  if (!/^\d{4}$/.test(bin)) {
    return { valid: false, error: 'Bin must be exactly 4 digits (e.g., 0001)' };
  }
  
  return { valid: true, error: null };
};

// Combined location input with single field
export const LocationInput = ({ 
  value, 
  onChange, 
  className = "",
  placeholder = "e.g., B1-1/0001",
  disabled = false,
  showLabel = true,
  label = "Location (Shelf/Bin)",
  size = "default" // "default" or "large"
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Sync local value with external value prop
  const effectiveValue = value !== undefined ? value : localValue;

  const handleChange = (e) => {
    let newValue = e.target.value.toUpperCase();
    
    setLocalValue(newValue);
    
    // Validate
    const validation = validateLocation(newValue);
    setError(validation.error);
    
    // Pass value up even if invalid (let parent decide)
    onChange(newValue);
  };

  const handleBlur = () => {
    // On blur, try to format the value properly
    if (effectiveValue && effectiveValue.includes('/')) {
      const { shelf, bin } = parseLocation(effectiveValue);
      if (shelf && bin) {
        const formatted = formatLocation(shelf, bin);
        setLocalValue(formatted);
        onChange(formatted);
        setError(null);
      }
    }
  };

  const inputClasses = size === "large" 
    ? "h-12 text-lg font-mono" 
    : "font-mono";

  return (
    <div className={className}>
      {showLabel && <Label>{label}</Label>}
      <Input
        ref={inputRef}
        value={effectiveValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`${inputClasses} ${error ? 'border-red-500' : ''} ${showLabel ? 'mt-1' : ''}`}
        data-testid="location-input"
      />
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};

// Split input with separate shelf and bin fields (for backward compatibility)
export const SplitLocationInput = ({
  shelfValue,
  binValue,
  onShelfChange,
  onBinChange,
  onSave,
  disabled = false,
  size = "default",
  shelfRef = null,
  binRef = null,
  autoFocusShelf = false,
  shelfDisabled = false
}) => {
  const internalShelfRef = useRef(null);
  const internalBinRef = useRef(null);
  
  // Use external ref if provided, otherwise use internal
  const shelfInputRef = shelfRef || internalShelfRef;
  const binInputRef = binRef || internalBinRef;

  // Auto-focus shelf when autoFocusShelf changes to true
  useEffect(() => {
    if (autoFocusShelf && shelfInputRef.current && !shelfDisabled) {
      shelfInputRef.current.focus();
      shelfInputRef.current.select();
    }
  }, [autoFocusShelf, shelfInputRef, shelfDisabled]);

  const handleShelfChange = (e) => {
    let value = e.target.value.toUpperCase();
    
    // Validate shelf format: letter(s) + number(s) + optional (dash + number(s))
    // Prevent letters after the first character(s) - second position onwards should allow only digits and dash
    // Examples: A1, B2, I0-1, H0-0, A12-5
    
    // Build the value character by character with validation
    let validated = '';
    let inPrefix = true;  // Start in letter prefix mode
    let afterDash = false;
    
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      
      if (inPrefix) {
        // First part: only letters allowed
        if (/[A-Z]/.test(char)) {
          validated += char;
        } else if (/[0-9]/.test(char)) {
          // Switch to number mode
          inPrefix = false;
          validated += char;
        }
        // Ignore other characters in prefix
      } else if (!afterDash) {
        // After prefix, before dash: only digits and dash allowed
        if (/[0-9]/.test(char)) {
          validated += char;
        } else if (char === '-') {
          afterDash = true;
          validated += char;
        }
        // Ignore letters here - this prevents IO instead of I0
      } else {
        // After dash: only digits allowed
        if (/[0-9]/.test(char)) {
          validated += char;
        }
        // Ignore letters after dash
      }
    }
    
    onShelfChange(validated);
    
    // Auto-advance to BIN after 4 characters (e.g., "H0-0", "B1-1")
    if (validated.length >= 4 && validated.includes('-')) {
      setTimeout(() => {
        binInputRef.current?.focus();
        binInputRef.current?.select();
      }, 50);
    }
  };

  const handleShelfKeyDown = (e) => {
    if (e.key === '/' || e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      binInputRef.current?.focus();
      binInputRef.current?.select();
    }
  };

  const handleBinChange = (e) => {
    // Only allow digits, max 4
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    onBinChange(value);
  };

  const handleBinKeyDown = (e) => {
    if (e.key === 'Enter' && onSave) {
      onSave();
    }
  };

  const handleBinBlur = () => {
    // Pad to 4 digits on blur
    if (binValue && binValue.length > 0) {
      const padded = binValue.padStart(4, '0');
      onBinChange(padded);
    }
  };

  const inputClasses = size === "large" 
    ? "h-12 text-lg font-mono" 
    : "font-mono";

  return (
    <div className="flex items-center gap-1">
      <div className="flex-1">
        <Label className="text-xs text-gray-500">
          {shelfDisabled ? "Shelf (HELD)" : "Shelf (4 chars → auto-advance)"}
        </Label>
        <Input
          ref={shelfInputRef}
          value={shelfValue}
          onChange={handleShelfChange}
          onKeyDown={handleShelfKeyDown}
          placeholder="H0-0"
          disabled={disabled || shelfDisabled}
          className={`${inputClasses} ${shelfDisabled ? 'bg-amber-100 border-amber-400 font-bold' : ''}`}
          maxLength={10}
          data-testid="shelf-input"
        />
      </div>
      <span className="text-2xl font-bold text-gray-400 mt-5">/</span>
      <div className="flex-1">
        <Label className="text-xs text-gray-500">Bin (Enter to save & next)</Label>
        <Input
          ref={binInputRef}
          value={binValue}
          onChange={handleBinChange}
          onKeyDown={handleBinKeyDown}
          onBlur={handleBinBlur}
          placeholder="0001"
          disabled={disabled}
          className={inputClasses}
          maxLength={4}
          data-testid="bin-input"
        />
      </div>
    </div>
  );
};

// Get unique locations from products for dropdown
export const getUniqueLocations = (products) => {
  const locations = new Set();
  
  products.forEach(p => {
    // Check for combined location field first
    if (p.location && p.location.includes('/')) {
      locations.add(p.location);
    } 
    // Then check for legacy shelf_number/bin_number
    else if (p.shelf_number || p.bin_number) {
      const location = formatLocation(p.shelf_number, p.bin_number);
      if (location) locations.add(location);
    }
    // Also check bin_location for backward compatibility
    else if (p.bin_location) {
      locations.add(p.bin_location);
    }
  });
  
  // Sort locations naturally (B1-1/0001 before B1-1/0002, B1-2/0001)
  return Array.from(locations).sort((a, b) => {
    const [shelfA, binA] = a.split('/');
    const [shelfB, binB] = b.split('/');
    
    const shelfCompare = (shelfA || '').localeCompare(shelfB || '', undefined, { numeric: true });
    if (shelfCompare !== 0) return shelfCompare;
    
    return (binA || '').localeCompare(binB || '', undefined, { numeric: true });
  });
};

// Get unique shelf codes (for filtering by shelf only)
export const getUniqueShelves = (products) => {
  const shelves = new Set();
  
  if (!products || !Array.isArray(products)) {
    return [];
  }
  
  products.forEach(p => {
    if (!p) return;
    
    // Check new location format first (e.g., "F0-0/0001")
    if (p.location && typeof p.location === 'string') {
      if (p.location.includes('/')) {
        const { shelf } = parseLocation(p.location);
        if (shelf) shelves.add(shelf);
      } else if (p.location.trim()) {
        // Location without "/" - could be just shelf code
        shelves.add(p.location.trim().toUpperCase());
      }
    }
    
    // Also check shelf_number field
    if (p.shelf_number && typeof p.shelf_number === 'string' && p.shelf_number.trim()) {
      shelves.add(p.shelf_number.trim().toUpperCase());
    }
    
    // Also check shelf field (some products might use this)
    if (p.shelf && typeof p.shelf === 'string' && p.shelf.trim()) {
      shelves.add(p.shelf.trim().toUpperCase());
    }
  });
  
  return Array.from(shelves).sort((a, b) => 
    a.localeCompare(b, undefined, { numeric: true })
  );
};

export default LocationInput;
